/**
 * Helpers around Google's certified CMP (AdSense "Privacy & messaging",
 * which implements IAB TCF v2.2). The CMP itself is delivered by the AdSense
 * loader in index.html; these helpers only talk to its public API.
 */

/** Anything push-compatible: the pre-load plain array or the CMP's live queue. */
type FcQueue = { push: (fn: () => void) => void };

type GoogleFcApi = {
  callbackQueue?: FcQueue;
  showRevocationMessage?: () => void;
};

declare global {
  interface Window {
    googlefc?: GoogleFcApi;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

/** True when AdSense (and therefore Google's CMP) is wired into this build. */
export function cmpAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.googlefc;
}

/**
 * Re-open Google's consent message so the visitor can review or withdraw
 * their choices. Safe to call before the CMP has finished loading - the
 * callbackQueue defers until it is ready. Returns false when the CMP is not
 * part of this build (no AdSense client configured) so callers can show an
 * explanatory message instead of a dead button.
 */
export function openConsentManager(): boolean {
  if (typeof window === 'undefined') return false;
  // No AdSense loader in this build means no CMP will ever arrive.
  if (!document.querySelector('script[src*="adsbygoogle"]')) return false;
  const fc = (window.googlefc = window.googlefc || {});
  // Standard Funding Choices pattern: a plain array the CMP drains when it
  // loads, so clicks that beat the script are queued, not lost.
  const queue: FcQueue = fc.callbackQueue ?? ([] as Array<() => void>);
  fc.callbackQueue = queue;
  queue.push(() => {
    window.googlefc?.showRevocationMessage?.();
  });
  return true;
}

/** Whether the browser is sending the Global Privacy Control signal. */
export function gpcActive(): boolean {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}
